// @flow
import { type Node, forwardRef, useEffect } from 'react';
import { OutboundLink } from 'gatsby-plugin-gtag';
import GatsbyLink from 'components/GatsbyLink';
import { getToggleModal, loadCachedModal } from 'components/Modals/context';
import type { UrlType } from 'components/types';
import { safeLink } from '../utils/safeLink';

type PropsType = {
  children?: Node,
  gaReference?: string,
  href?: string,
  link?: UrlType,
  modalDynamicOptions?: { [string]: any },
  onClick?: Function,
  to?: string,
  [string]: string
};

export function isValidLink(link?: UrlType) {
  if (!link) return false;
  // eslint-disable-next-line camelcase
  const isValidStory = !!link.story?.full_slug;
  return link.url || link.cached_url || link.email || isValidStory;
}

const Link = forwardRef<
  PropsType,
  HTMLAnchorElement | HTMLButtonElement | HTMLSpanElement
>(
  (
    {
      link,
      to,
      href,
      children,
      gaReference,
      /* eslint-disable react/prop-types */
      // These props are to prevent react warnings from Card props
      cardAlign,
      // These props are to prevent react warnings from @tablekit/Button props
      iconBefore,
      iconAfter,
      isOnlyChild,
      shouldFitContainer,
      modalDynamicOptions,
      /* eslint-enable react/prop-types */
      ...others
    }: PropsType,
    ref: *
  ) => {
    const toggleModal = getToggleModal();
    if (!to && !href && !others.onClick && !isValidLink(link)) {
      return (
        <span {...others} ref={ref}>
          {children}
        </span>
      );
    }
    const { url, cached_url: cachedUrl, linktype, email, story } = link || {};
    if (to || linktype === 'story') {
      // for this resolution priority order see
      // https://github.com/storyblok/storyblok/issues/174 and https://www.storyblok.com/cl/url-resolving
      // eslint-disable-next-line camelcase
      const toLink = story?.full_slug || cachedUrl || url || to || '';
      const text = children || story?.name;
      if (toLink.match(/[^/]+\/modals\//)) {
        useEffect(() => {
          loadCachedModal(toLink);
        }, []);
        return (
          <button
            {...others}
            data-modal-link={toLink}
            type="button"
            onClick={() => toggleModal(toLink, false, modalDynamicOptions)}
            ref={ref}
          >
            {text}
          </button>
        );
      }
      return (
        <GatsbyLink to={safeLink(toLink)} {...others} ref={ref}>
          {text}
        </GatsbyLink>
      );
    }
    if (linktype === 'asset') {
      return (
        <a
          href={cachedUrl || url}
          {...others}
          target="_blank"
          rel="noopener noreferrer"
          ref={ref}
        >
          {children}
        </a>
      );
    }
    if (linktype === 'email') {
      return (
        <a href={`mailto:${email}`} {...others} ref={ref}>
          {children}
        </a>
      );
    }
    return (
      <OutboundLink href={cachedUrl || url || href} {...others}>
        {children}
      </OutboundLink>
    );
  }
);

export default Link;
